/**
 * 预设全局状态管理上下文
 * 用于在应用中共享当前激活的预设信息
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getActivePreset } from '../api/presetApi';
import { getCurrentUser } from '../api';
import Logger from '../utils/logger';

const PresetContext = createContext(null);

export const PresetProvider = ({ children }) => {
  const [activePreset, setActivePreset] = useState(null);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastMode, setLastMode] = useState(null);

  /**
   * 加载当前激活的预设
   * @param {string} mode - 可选，当前模式
   */
  const loadActivePreset = useCallback(async (mode = null) => {
    const currentUser = getCurrentUser();
    if (!currentUser?.email) {
      setActivePreset(null);
      setGeneratedPrompt('');
      return;
    }

    // 如果模式没变，且已经加载过，不重复加载
    if (mode === lastMode && activePreset !== undefined) {
      return;
    }

    try {
      setLoading(true);
      const result = await getActivePreset(currentUser.email, mode);
      
      if (result.success && result.data) {
        setActivePreset(result.data.preset);
        setGeneratedPrompt(result.data.generated_prompt || '');
        setLastMode(mode);
        Logger.info('已加载激活预设', { 
          presetName: result.data.preset?.name,
          mode 
        });
      } else {
        setActivePreset(null);
        setGeneratedPrompt('');
      }
    } catch (err) {
      Logger.error('加载激活预设失败', err);
      setActivePreset(null);
      setGeneratedPrompt('');
    } finally {
      setLoading(false);
    }
  }, [lastMode, activePreset]);

  /**
   * 刷新预设（强制重新加载）
   */
  const refreshPreset = useCallback(async (mode = null) => {
    setLastMode(null); // 重置lastMode以强制重新加载
    await loadActivePreset(mode);
  }, [loadActivePreset]);

  /**
   * 清除预设状态（用于登出时）
   */
  const clearPreset = useCallback(() => {
    setActivePreset(null);
    setGeneratedPrompt('');
    setLastMode(null);
  }, []);

  /**
   * 获取包装后的用户输入
   * 将用户输入与预设提示词组合
   * @param {string} userInput - 用户原始输入
   * @param {string} mode - 当前模式
   * @returns {string} - 包装后的输入
   */
  const wrapUserInput = useCallback((userInput, mode) => {
    if (!activePreset || !generatedPrompt) {
      return userInput;
    }

    // 检查预设是否适用于当前模式
    const applicableModes = activePreset.applicable_modes || [];
    if (applicableModes.length > 0 && !applicableModes.includes(mode)) {
      return userInput;
    }

    // 将预设提示词附加到用户输入
    // 预设提示词放在前面，作为上下文设置
    return `${generatedPrompt}\n【用户需求】\n${userInput}`;
  }, [activePreset, generatedPrompt]);

  /**
   * 检查当前模式是否有适用的预设
   * @param {string} mode - 当前模式
   * @returns {boolean}
   */
  const hasApplicablePreset = useCallback((mode) => {
    if (!activePreset) return false;
    
    const applicableModes = activePreset.applicable_modes || [];
    // 如果没有设置适用模式，则适用于所有模式
    if (applicableModes.length === 0) return true;
    
    return applicableModes.includes(mode);
  }, [activePreset]);

  // 初始加载
  useEffect(() => {
    const currentUser = getCurrentUser();
    if (currentUser?.email) {
      loadActivePreset();
    }
  }, []);

  const value = {
    activePreset,
    generatedPrompt,
    loading,
    loadActivePreset,
    refreshPreset,
    clearPreset,
    wrapUserInput,
    hasApplicablePreset,
  };

  return (
    <PresetContext.Provider value={value}>
      {children}
    </PresetContext.Provider>
  );
};

/**
 * 使用预设上下文的Hook
 */
export const usePreset = () => {
  const context = useContext(PresetContext);
  if (!context) {
    throw new Error('usePreset must be used within a PresetProvider');
  }
  return context;
};

export default PresetContext;
